/*
 * Copyright (C) 2024 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and
 * should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly
 * prohibited.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-underscore-dangle */
import { applyMiddleware, createStore, Store } from 'redux';
import thunk from 'redux-thunk';
import { routerMiddleware } from 'connected-react-router';
import { createBrowserHistory } from 'history';
import { createEpicMiddleware } from 'redux-observable';
import { ajax as rxAjax, AjaxConfig, AjaxResponse } from 'rxjs/ajax';
import { catchError } from 'rxjs/operators';
import { Observable, Observer, of } from 'rxjs';
import axios, { AxiosError } from 'axios';
import get from 'lodash/get';
import rootReducer, { rootEpic } from '../store';
import { ActionCreators as SessionCreators } from '../store/session/actions';
import { unset } from 'lodash';
import { composeWithDevTools } from 'redux-devtools-extension';
import { isMockMode } from './GlobalUtils';

export interface ApiHeaders {
  'Content-Type': string;
  'x-role'?: string;
  Accept: string;
  Authorization?: string;
}

let store: Store | null = null;
export const apiHeaders: ApiHeaders = {
  'Content-Type': 'application/json',
  Accept: 'application/json',
};

const { NODE_ENV } = import.meta.env;
const history = createBrowserHistory();
const router = routerMiddleware(history);

const isMock = (useMockExplicitly: boolean, path: string): boolean =>
  path.startsWith('/data/embed/url') || path.startsWith('/ux/meta') || path.startsWith('/data/queries')
    ? false
    : useMockExplicitly || import.meta.env.VITE_INSIGHTS_API_MOCK === 'true';

const makeUrl = (mock: boolean = false, path: string): string => {
  return `${import.meta.env.VITE_INSIGHTS_API_BASE_URL}/api${isMock(mock, path) ? '/mockapi' : ''}${path}`;
};

const makeBasePathUrl = (mock: boolean = false, path: string): string => {
  return `${import.meta.env.VITE_INSIGHTS_API_BASE_URL}${isMock(mock, path) ? '/api/mockapi' : ''}${path}`;
};

const makeSseUrl = (path: string): string => {
  return import.meta.env.VITE_INSIGHTS_SSE_BASE_URL + path;
};

export const storeAccess = {
  set: (s: Store) => {
    store = s;
  },
  get: () => store,
};

const _rxjsAjaxErrorFilter = (ajaxCall: Observable<{} | AjaxResponse<any>>): Observable<{} | AjaxResponse<any>> => {
  return ajaxCall.pipe(
    catchError(error => {
      if (error && error.status === 401) {
        const tempStore = storeAccess.get();
        if (tempStore) {
          tempStore.dispatch(SessionCreators.SignOut.create({}));
        }
        // TODO throwing a new error means dealing with this in *every* catchError methods of observers :()
        // return Observable.throw({...error, action: {type: (...?...)), error}});
        // TODO however returning type of SignOutUser doesn't get detected by observer for some reason, so have to call dispatch directly instead :(
        return of({ type: SessionCreators.SignOut.type });
      }
      return ajaxCall;
    }),
  );
};

// Needed to encode username, password, and grant_type of authentication requests for x-www-form-urlencoded content-type
const toUrlEncoded = (obj: any) =>
  Object.keys(obj)
    .map(k => `${encodeURIComponent(k)}=${encodeURIComponent(obj[k])}`)
    .join('&');

/**
 * An interceptor that will either call the ajax call directly or refresh the API token if it is expired then call the ajax call.
 * @param ajaxCallType the original ajax call for a get or delete that will be called in this function
 * @param path the url path to pass to the ajax call
 * @param headers the API headers to pass to the ajax call
 */
const ajaxTokenRefreshInterceptorWithoutBody = (
  ajaxCallType: (request: AjaxConfig) => Observable<AjaxResponse<any>>,
  url: string,
  method: string,
  headers: any,
): Observable<AjaxResponse<any>> => {
  return ajaxCallType({ url, method, headers, crossDomain: true });
};

/**
 * An interceptor that will either call the ajax call directly or refresh the API token if it is expired then call the ajax call.
 * @param ajaxCallType the original ajax call for a put or post that will be called in this function
 * @param path the url path to pass to the ajax call
 * @param body the body of the api request to pass to the ajax call
 * @param headers the API headers to pass to the ajax call
 */
const ajaxTokenRefreshInterceptorWithBody = (
  ajaxCallType: (request: AjaxConfig) => Observable<AjaxResponse<any>>,
  url: string,
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  body: any,
  headers: any,
): Observable<AjaxResponse<any>> => {
  //multiple files
  if (Array.isArray(body) && body.length && body[0] instanceof File) {
    // Removed Content-Type; browser will update Content-Type
    unset(headers, 'Content-Type');
    headers['Accept'] = '*/*';

    const formData = new FormData();
    body.forEach(file => formData.append('file', file));
    body = formData;
  } else if (body instanceof File) {
    // Removed Content-Type; browser will update Content-Type
    unset(headers, 'Content-Type');

    headers['Accept'] = '*/*';

    // Convert file to form data
    const formData = new FormData();
    formData.append('file', body);
    body = formData;
  } else if (body && typeof JSON.parse(body) === 'string') {
    headers['Content-Type'] = 'text/plain';
    headers['Accept'] = 'text/plain';

    body = JSON.parse(body);
  }

  return ajaxCallType({ url, method, body, headers, crossDomain: true });
};

/**
 * An interceptor that will either call the ajax call directly or refresh the API token if it is expired then call the ajax call.
 * @param ajaxCallType the original ajax call for a get or delete that will be called in this function
 * @param path the url path to pass to the ajax call
 * @param headers the API headers to pass to the ajax call
 */
const ajaxTokenRefreshInterceptorWithoutBodyForCsvResponse = (
  ajaxCallType: (request: AjaxConfig) => Observable<AjaxResponse<any>>,
  url: string,
  method: string,
  headers: any,
): Observable<AjaxResponse<any>> => {
  // Add necessary headers for CSV response
  headers['Accept'] = 'text/csv';
  headers['Content-Type'] = 'text/csv';

  return ajaxCallType({ url, method, headers, crossDomain: true, responseType: 'text' });
};

// ajax calls with a refresh token interceptor (except for the calls to the auth service)
export const ajaxRaw = {
  auth: (path: string, body: any, headers: any, mock: boolean = false) =>
    // Note: do not include the token refresh interceptor for auth calls
    rxAjax({
      url: makeBasePathUrl(mock, path),
      body: body ? toUrlEncoded(body) : undefined,
      headers: headers || apiHeaders,
      method: 'POST',
      crossDomain: true,
    }),
  post: (path: string, body: any, mock: boolean = false) =>
    ajaxTokenRefreshInterceptorWithBody(
      rxAjax,
      makeUrl(mock, path),
      'POST',
      body ? JSON.stringify(body) : undefined,
      apiHeaders,
    ),
  postToBasePath: (path: string, body: any, mock: boolean = false) =>
    ajaxTokenRefreshInterceptorWithBody(
      rxAjax,
      makeBasePathUrl(mock, path),
      'POST',
      body ? JSON.stringify(body) : undefined,
      apiHeaders,
    ),
  postJson: (path: string, body: any, mock: boolean = false) =>
    ajaxTokenRefreshInterceptorWithBody(rxAjax, makeUrl(mock, path), 'POST', body, apiHeaders),
  putWithHeaders: (path: string, body: any, appendedHeaders: any, mock: boolean = false) =>
    ajaxTokenRefreshInterceptorWithBody(rxAjax, makeUrl(mock, path), 'PUT', body ? JSON.stringify(body) : undefined, {
      ...apiHeaders,
      ...appendedHeaders,
    }),
  postWithHeaders: (path: string, body: any, appendedHeaders: any, mock: boolean = false) =>
    ajaxTokenRefreshInterceptorWithBody(rxAjax, makeUrl(mock, path), 'POST', body ? JSON.stringify(body) : undefined, {
      ...apiHeaders,
      ...appendedHeaders,
    }),
  postFilesWithHeaders: (path: string, files: File[], appendedHeaders: any, mock: boolean = false) =>
    ajaxTokenRefreshInterceptorWithBody(rxAjax, makeUrl(mock, path), 'POST', files ? files : undefined, {
      ...apiHeaders,
      ...appendedHeaders,
    }),
  get: (path: string, mock: boolean = false) =>
    ajaxTokenRefreshInterceptorWithoutBody(rxAjax, makeUrl(mock, path), 'GET', apiHeaders),
  getWithHeaders: (path: string, appendedHeaders: any, mock: boolean = false) =>
    ajaxTokenRefreshInterceptorWithoutBody(rxAjax, makeUrl(mock, path), 'GET', { ...apiHeaders, ...appendedHeaders }),
  getFromBasePath: (path: string, mock: boolean = false) =>
    ajaxTokenRefreshInterceptorWithoutBody(rxAjax, makeBasePathUrl(mock, path), 'GET', apiHeaders),
  getCsvWithHeaders: (path: string, appendedHeaders: any, mock: boolean = false) =>
    ajaxTokenRefreshInterceptorWithoutBodyForCsvResponse(rxAjax, makeUrl(mock, path), 'GET', {
      ...apiHeaders,
      ...appendedHeaders,
    }),
  put: (path: string, body: any, mock: boolean = false) =>
    ajaxTokenRefreshInterceptorWithBody(rxAjax, makeUrl(mock, path), 'PUT', body, apiHeaders),
  delete: (path: string, mock: boolean = false) =>
    ajaxTokenRefreshInterceptorWithoutBody(rxAjax, makeUrl(mock, path), 'DELETE', apiHeaders),
  deleteWithHeaders: (path: string, body: any, appendedHeaders: any, mock: boolean = false) =>
    ajaxTokenRefreshInterceptorWithoutBody(rxAjax, makeUrl(mock, path), 'DELETE', {
      ...apiHeaders,
      ...appendedHeaders,
    }),
};

export const ajax = {
  post: (path: string, body: any, mock: boolean = false) => _rxjsAjaxErrorFilter(ajaxRaw.post(path, body, mock)),
  get: (path: string, mock: boolean = false) => _rxjsAjaxErrorFilter(ajaxRaw.get(path, mock)),
  put: (path: string, body: any, mock: boolean = false) => _rxjsAjaxErrorFilter(ajaxRaw.put(path, body, mock)),
  delete: (path: string, mock: boolean = false) => _rxjsAjaxErrorFilter(ajaxRaw.delete(path, mock)),
};

export const sse = {
  get: (path: string) =>
    Observable.create((observer: Observer<{}>) => {
      const eventSource = new EventSource(makeSseUrl(path));
      eventSource.onmessage = message => observer.next(JSON.parse(message.data));
      eventSource.onerror = message => observer.error(message);

      return () => {
        eventSource.close();
      };
    }),
};

export default function configureStore(initialState: any): { store: Store; history: History } {
  const epicMiddleware = createEpicMiddleware({
    dependencies: {
      ajax,
      ajaxRaw,
      sse,
    },
  });

  const composeEnhancers = composeWithDevTools({
    // enable tracing of Redux state changes when running in mock mode
    trace: isMockMode(),
  });

  const middleware = [epicMiddleware, thunk, router];

  if (NODE_ENV === 'development' && import.meta.env.VITE_REDUX_LOGGER_ENABLED === 'true') {
    // eslint-disable-next-line global-require, import/no-extraneous-dependencies, @typescript-eslint/no-var-requires
    const { logger } = require('redux-logger');

    middleware.push(logger);
  }

  const reduxStore = createStore(
    rootReducer(history),
    initialState,
    NODE_ENV === 'production' || NODE_ENV === 'test'
      ? applyMiddleware(...middleware)
      : composeEnhancers(applyMiddleware(...middleware)),
  );

  epicMiddleware.run(rootEpic);

  return {
    store: reduxStore,
    history,
  };
}

export const initStore = (): { store: Store; history: History } => {
  const storeInfo = configureStore({});
  storeAccess.set(storeInfo.store);

  setupAxios(() => {
    storeInfo.store.dispatch(SessionCreators.SignOut.create());
  });

  return storeInfo;
};

axios.defaults.baseURL = makeUrl(false, '');

/**
 * Removes agents from axios configs, to prevent circular references
 */
function removeAgents(axios: any, config: any) {
  if (axios.defaults.agent === config.agent) {
    delete config.agent;
  }
  if (axios.defaults.httpAgent === config.httpAgent) {
    delete config.httpAgent;
  }
  if (axios.defaults.httpsAgent === config.httpsAgent) {
    delete config.httpsAgent;
  }
}

/**
 * Setup axios for default api url and error handling.
 */
const setupAxios = (onUnauthenticated: () => any) => {
  /**
   * Check all response for and handle authorization errors.
   */
  const onResponseError = async (responseError: AxiosError) => {
    const config = responseError.config;
    const status = get(responseError, 'status', null) || get(responseError, 'response.status', null);
    const missingConfig = !config;

    // if a 401 happens the user is no longer authorized. this should never happen
    // as Okta will continuously refresh tokens in the background. if that fails,
    // the user should be logged-out.
    if (status === 401) {
      if (missingConfig) throw responseError;

      /** Pulled from https://github.com/softonic/axios-retry/blob/master/es/index.js */
      removeAgents(axios, config);

      onUnauthenticated();
    }

    throw responseError;
  };

  /**
   * Allow successful responses to pass through;
   */
  const onResponseSuccess = (response: any) => response;

  axios.interceptors.response.use(onResponseSuccess, onResponseError);
};
